<template>
  <div class="mobileViewStyle role-detail">
    <!-- 系统管理-角色详情（企业微信手机端，仅查看） -->
    <div class="detail-body" v-loading="loading">
      <list-card :label="detail.roleName || '角色信息'">
        <template #desc>
          <span class="type-tag" :class="Number(detail.roleType) === 1 ? 'admin' : 'normal'">
            {{ Number(detail.roleType) === 1 ? "超级管理员" : "普通角色" }}
          </span>
        </template>
        <data-column label="角色名称">{{ detail.roleName || "—" }}</data-column>
        <data-column label="角色分类">{{ Number(detail.roleType) === 1 ? "超级管理员" : "普通角色" }}</data-column>
        <data-column label="岗位">{{ businessTypeName(detail.businessType) || "—" }}</data-column>
        <data-column label="角色描述">{{ detail.roleDescription || "—" }}</data-column>
        <data-column label="创建时间">{{ dateTime(detail.createdTime) }}</data-column>
      </list-card>

      <!-- 已授权功能按菜单树展示：父级菜单带出下级，授权项打勾 -->
      <list-card class="module-card" :label="'已授权功能（' + grantedCount + '）'">
        <div v-if="moduleRows.length === 0" class="empty">该角色暂无授权功能</div>
        <div v-else class="menu-tree">
          <div class="tree-row" v-for="node in moduleRows" :key="node.id"
               :style="{paddingLeft: (node.depth * 16 + 4) + 'px'}">
            <yq-icon class="node-icon" :name="node.children.length ? 'element-ui_arrow-down' : 'element-ui_document'"/>
            <span class="node-name" :class="{parent: node.children.length > 0, granted: node.granted}">
              {{ node.moduleName }}
            </span>
            <yq-icon v-if="node.granted" class="granted-icon" name="element-ui_check"/>
          </div>
        </div>
      </list-card>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import roleApi from "@/api/normal/sys/roleApi";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "RoleDetailQW",
  components: {
    YqIcon, DataColumn, ListCard
  },
  data() {
    return {
      loading: false,
      detail: {},
      businessTypeList: [],
      allModules: [],
      grantedIds: [],
    };
  },
  computed: {
    /** 只保留「自身被授权 + 含被授权下级」的节点，按菜单层级压平成可渲染的行 */
    moduleRows() {
      const granted = new Set(this.grantedIds);
      const rows = [];
      const walk = (list, depth) => {
        (list || []).forEach(node => {
          rows.push({id: node.id, moduleName: node.moduleName, granted: node.granted, children: node.children, depth});
          walk(node.children, depth + 1);
        });
      };
      walk(this.filterGranted(this.allModules, granted), 0);
      return rows;
    },
    grantedCount() {
      return this.grantedIds.length;
    },
  },
  created() {
    const roleId = this.route.query.id;
    if (!roleId) {
      return;
    }
    this.loading = true;
    commonApi.queryUniqueDictionaryList("POSITION_ROLE").then(list => this.businessTypeList = list || []);
    Util.getUserInfo().then(user => {
      const merchantId = user.merchantId;
      const rolePromise = roleApi.queryAllRoles(merchantId).then(list => {
        this.detail = (list || []).find(role => role.id === roleId) || {};
      });
      const modulePromise = roleApi.queryAllModules(merchantId);
      const grantPromise = roleApi.queryGrantModules(roleId);
      Promise.all([rolePromise, modulePromise, grantPromise]).then(([, modules, grants]) => {
        this.allModules = modules || [];
        this.grantedIds = (grants || []).map(grant => grant.moduleId).filter(id => id);
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    });
  },
  methods: {
    businessTypeName(code) {
      if (!code) {
        return "";
      }
      const item = this.businessTypeList.find(t => t.dictCode === code);
      return item ? item.dictName : code;
    },
    /** 保留自身被授权或含被授权下级的节点，形成「已授权功能」菜单树 */
    filterGranted(list, granted) {
      const result = [];
      (list || []).forEach(module => {
        const children = this.filterGranted(module.children || [], granted);
        if (granted.has(module.id) || children.length > 0) {
          result.push({
            id: module.id,
            moduleName: module.moduleName,
            granted: granted.has(module.id),
            children,
          });
        }
      });
      return result;
    },
    dateTime(value) {
      return value ? String(value).slice(0, 16) : "—";
    },
  }
};
</script>

<style lang="scss" scoped>
.role-detail{
  height: 100%;
  overflow: auto;
  .detail-body{
    padding: 10px;
  }
  .type-tag{
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.admin{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
    &.normal{
      color: var(--el-color-primary);
      background: var(--el-color-primary-light-9);
    }
  }
  .module-card{
    display: block;
    margin-top: 10px;
    .menu-tree{
      padding-top: 6px;
    }
    .tree-row{
      display: flex;
      align-items: center;
      min-height: 34px;
      border-bottom: 1px solid var(--yq-background-color);
      font-size: var(--yq-font-size-mini-mobile);
      &:last-child{
        border-bottom: none;
      }
      .node-icon{
        flex: none;
        margin-right: 6px;
        color: var(--yq-font-color-desc);
        font-size: var(--yq-font-size-mini-mobile);
      }
      .node-name{
        flex: 1;
        min-width: 0;
        color: var(--yq-font-color-base);
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        &.parent{
          color: var(--yq-font-color-desc);
          font-weight: 600;
        }
        &.granted{
          color: var(--el-color-primary);
        }
      }
      .granted-icon{
        flex: none;
        margin-left: 6px;
        color: var(--el-color-success);
      }
    }
    .empty{
      padding: 16px 0;
      text-align: center;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-desc);
    }
  }
}
</style>
